import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { StarRating } from "../components/fp/StarRating";
import { HeartHandshake, CheckCircle2, MessageSquare, Loader2 } from "lucide-react";

export default function FPMinhasAvaliacoes() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/fp/reviews/mine").then((r) => setPending(r.data))
      .catch(() => setPending([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-my-reviews">
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-700 font-bold">
        <HeartHandshake className="w-3.5 h-3.5" /> Filho Presente · Avaliações
      </div>
      <h1 className="font-display text-3xl font-bold text-[#0B2B62] mt-2">Avaliações pendentes</h1>
      <p className="text-slate-600 text-sm mt-1 max-w-2xl">
        Depois que uma consulta ou exame é concluído, sua avaliação ajuda outras famílias a escolherem bem.
      </p>

      <div className="mt-6">
        {loading ? <div className="text-slate-500">Carregando…</div>
          : pending.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-10 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <div className="text-slate-700 font-medium">Você não tem avaliações pendentes.</div>
              <div className="text-xs text-slate-500 mt-1">Assim que uma consulta for realizada, ela aparece aqui.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {pending.map((r) => (
                <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid={`fp-review-pending-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800">
                      {r.type === "consulta" ? "Consulta" : r.type === "exame" ? "Exame" : "Solicitação"} · {r.professional_name || r.specialty || r.exam_type || "—"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Beneficiário: {r.beneficiary_name}
                      {r.completed_at && <> · Realizada em {new Date(r.completed_at).toLocaleDateString("pt-BR")}</>}
                    </div>
                  </div>
                  <button onClick={() => setTarget(r)} data-testid={`fp-review-cta-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs bg-amber-400 text-[#0B2B62] font-semibold px-4 py-2 rounded-md hover:bg-amber-300">
                    <MessageSquare className="w-3.5 h-3.5" /> Avaliar
                  </button>
                </div>
              ))}
            </div>
          )}
      </div>

      {target && (
        <ReviewFormModal req={target} onClose={() => setTarget(null)}
          onDone={() => { setTarget(null); load(); }} />
      )}
    </div>
  );
}

const ReviewFormModal = ({ req, onClose, onDone }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Escolha de 1 a 5 estrelas.");
    setSaving(true);
    try {
      await api.post("/fp/reviews", {
        request_id: req.id, rating, comment: comment.trim() || null,
      });
      toast.success("Avaliação enviada. Obrigado!");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="fp-review-modal"
        className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Sua avaliação</div>
        <div className="font-display text-lg font-bold text-[#0B2B62] mt-1">
          {req.professional_name || req.specialty || "Solicitação"}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">Beneficiário: {req.beneficiary_name}</div>

        <div className="mt-5 flex flex-col items-center gap-2">
          <StarRating value={rating} onChange={setRating} size={32} testid="fp-review-stars" />
          <div className="text-xs text-slate-500">
            {rating === 0 ? "Selecione uma nota" :
             rating <= 2 ? "Pode melhorar" :
             rating === 3 ? "Foi ok" :
             rating === 4 ? "Muito bom" : "Excelente!"}
          </div>
        </div>

        <label className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mt-5 mb-1">Comentário (opcional)</label>
        <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
          data-testid="fp-review-comment" maxLength={800}
          placeholder="Como foi a experiência? Isso ajuda outras famílias."
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button>
          <button type="submit" disabled={saving || !rating} data-testid="fp-review-submit"
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Enviar avaliação
          </button>
        </div>
      </form>
    </div>
  );
};
